import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Stack,
  TextField,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Close,
  Delete,
  PlayArrow,
  AccountTree,
  CallSplit,
  DragIndicator,
  Functions,
  Loop as LoopIcon,
} from "@mui/icons-material";
import { HelpCircle } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import agentService from "../../services/agentService";
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";
import styles from "./AgentChainingDialog.module.scss";

// Define nodeTypes outside the component to prevent recreation on every render
const nodeTypes = {
  custom: ({ data }) => (
    <div className={styles.customNode}>
      <div className={styles.nodeLabel}>{data.label}</div>
    </div>
  ),
};

const PYTHON_EXEC_TYPE = "python_exec";
const PYTHON_NODE_NAME = "Calculator";
const PYTHON_DEFAULT_TIMEOUT_SECONDS = 10;
const PYTHON_MAX_OUTPUT_BYTES = 65536;
const PYTHON_DEFAULT_LOAD_FILES = false;

const isPythonExecStep = (step) =>
  step?.executionKind === PYTHON_EXEC_TYPE || step?.type === PYTHON_EXEC_TYPE;

const clampPythonTimeout = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return PYTHON_DEFAULT_TIMEOUT_SECONDS;
  }

  return Math.min(60, Math.max(1, Math.round(parsed)));
};

const buildPythonConfigJson = (
  timeoutSeconds = PYTHON_DEFAULT_TIMEOUT_SECONDS,
  loadFiles = PYTHON_DEFAULT_LOAD_FILES
) =>
  JSON.stringify({
    timeout_seconds: clampPythonTimeout(timeoutSeconds),
    max_output_bytes: PYTHON_MAX_OUTPUT_BYTES,
    load_files: Boolean(loadFiles),
  });

const readPythonConfigJson = (configJson) => {
  const fallback = {
    timeout_seconds: PYTHON_DEFAULT_TIMEOUT_SECONDS,
    max_output_bytes: PYTHON_MAX_OUTPUT_BYTES,
    load_files: PYTHON_DEFAULT_LOAD_FILES,
  };

  if (typeof configJson !== "string" || !configJson.trim()) {
    return { isValid: false, config: fallback };
  }

  try {
    const parsed = JSON.parse(configJson);

    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return { isValid: false, config: fallback };
    }

    const timeoutSeconds = Number(parsed.timeout_seconds);
    const maxOutputBytes = Number(parsed.max_output_bytes);

    if (
      !Number.isInteger(timeoutSeconds) ||
      timeoutSeconds < 1 ||
      timeoutSeconds > 60 ||
      !Number.isInteger(maxOutputBytes) ||
      maxOutputBytes <= 0
    ) {
      return { isValid: false, config: fallback };
    }

    return {
      isValid: true,
      config: {
        timeout_seconds: timeoutSeconds,
        max_output_bytes: maxOutputBytes,
        load_files: Boolean(parsed.load_files),
      },
    };
  } catch (error) {
    return { isValid: false, config: fallback };
  }
};

const LOOP_EXEC_TYPE = "loop";

const isLoopStep = (step) =>
  step?.executionKind === LOOP_EXEC_TYPE ||
  step?.execution_kind === LOOP_EXEC_TYPE ||
  step?.type === LOOP_EXEC_TYPE;

// Backend defaults/clamps for loop node config (see Loop Node batch jobs spec).
const LOOP_CONFIG_DEFAULTS = {
  item_prompt: "",
  concurrency: 1,
  micro_batch_size: 1,
  chunk_size: 50,
  max_item_attempts: 2,
  on_error: "skip",
  max_items: 5000,
  inline_threshold: 2,
  sink_mode: "post_chain",
};

const LOOP_CONFIG_LIMITS = {
  concurrency: [1, 10],
  micro_batch_size: [1, 25],
  chunk_size: [10, 200],
  max_item_attempts: [1, 5],
  max_items: [1, 50000],
  inline_threshold: [0, 100],
};

const clampLoopInt = (value, field) => {
  const [min, max] = LOOP_CONFIG_LIMITS[field];
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return LOOP_CONFIG_DEFAULTS[field];
  }
  return Math.min(max, Math.max(min, Math.round(parsed)));
};

const getLoopConfigJsonString = (node) =>
  node?.configJson ?? node?.config_json ?? null;

const readLoopConfigJson = (configJson) => {
  const fallback = { ...LOOP_CONFIG_DEFAULTS };

  if (typeof configJson !== "string" || !configJson.trim()) {
    return fallback;
  }

  try {
    const parsed = JSON.parse(configJson);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return fallback;
    }

    const config = { ...fallback };
    Object.keys(LOOP_CONFIG_LIMITS).forEach((field) => {
      if (parsed[field] !== undefined) {
        config[field] = clampLoopInt(parsed[field], field);
      }
    });
    if (typeof parsed.item_prompt === "string") {
      config.item_prompt = parsed.item_prompt;
    }
    if (parsed.on_error === "skip" || parsed.on_error === "fail_job") {
      config.on_error = parsed.on_error;
    }
    const sinkMode = parsed.sink?.mode;
    if (sinkMode === "post_chain" || sinkMode === "none") {
      config.sink_mode = sinkMode;
    }
    return config;
  } catch (error) {
    return fallback;
  }
};

const buildLoopConfigJson = (config = {}) => {
  const merged = { ...LOOP_CONFIG_DEFAULTS, ...config };
  const payload = {
    concurrency: clampLoopInt(merged.concurrency, "concurrency"),
    micro_batch_size: clampLoopInt(merged.micro_batch_size, "micro_batch_size"),
    chunk_size: clampLoopInt(merged.chunk_size, "chunk_size"),
    max_item_attempts: clampLoopInt(merged.max_item_attempts, "max_item_attempts"),
    on_error: merged.on_error === "fail_job" ? "fail_job" : "skip",
    max_items: clampLoopInt(merged.max_items, "max_items"),
    inline_threshold: clampLoopInt(merged.inline_threshold, "inline_threshold"),
    sink: { mode: merged.sink_mode === "none" ? "none" : "post_chain" },
  };
  if (typeof merged.item_prompt === "string" && merged.item_prompt.trim() !== "") {
    payload.item_prompt = merged.item_prompt;
  }
  return JSON.stringify(payload);
};

// Plain-language help for every loop config control (shown via InfoTooltip,
// matching the field-help pattern used in AgentCreate.jsx). The running example
// is a 30-company dataset from the previous step.
const LOOP_FIELD_HELP = {
  item_prompt: {
    title: "Per-record prompt template",
    body: [
      "The instruction sent to the agent for each single record. The {{…}} placeholders are replaced per record: {{item}} (whole record as JSON), {{item.field.subfield}}, {{index}} (1-based), {{total}}.",
      "Leave it empty and the node's description box is used instead.",
    ],
    example:
      'For record {"name": "Acme", "url": "acme.com"}, the template "Analyze {{item.name}} at {{item.url}} — {{index}} of {{total}}" becomes "Analyze Acme at acme.com — 1 of 30".',
  },
  concurrency: {
    title: "Concurrency",
    body: [
      "How many records are processed at the same time. With 1, companies are analyzed one at a time; as one finishes, the next starts.",
      "Higher = faster, but more pressure on your LLM rate limits. 1 is a safe default.",
    ],
  },
  micro_batch_size: {
    title: "Micro-batch size",
    body: [
      "How many records share one LLM call. At 1, every company gets its own call (30 records = 30 calls).",
      "At 10, ten companies are packed into one prompt and the model must return 10 answers (only 3 calls — about 10x cheaper, but if the model formats the reply badly, the system falls back to one-by-one for that group).",
      "Keep 1 for quality; raise it to save cost on simple tasks.",
    ],
  },
  chunk_size: {
    title: "Chunk size (advanced)",
    body: [
      "How many records one background task handles before handing over to the next task. It's like reading a book 50 pages at a time with a bookmark: if the worker crashes or redeploys, you lose at most the current chunk's progress, never the whole job.",
      "Most users never need to change this — that's why it's marked advanced.",
    ],
    example: "A 30-record dataset with chunk size 10 = 3 chunks.",
  },
  max_item_attempts: {
    title: "Max attempts per record",
    body: [
      "If analyzing a record fails (LLM error, scrape timeout), it is automatically retried. After this many total attempts it is marked failed and the job moves on.",
      'Failed records can be re-run later with the "Retry failed" button on the job page.',
    ],
  },
  max_items: {
    title: "Max items",
    body: [
      "Safety cap. If the previous step accidentally returns 80,000 rows, only the first 5,000 are processed and the rest are dropped (the job records that truncation happened).",
      "Protects against runaway cost.",
    ],
  },
  inline_threshold: {
    title: "Inline threshold",
    body: [
      'The "is this even worth a background job?" line.',
      "2 records ≤ 2 → runs immediately inside the request and the answer comes back like a normal chat reply — no job created.",
      "5 records > 2 → becomes a background batch job and you get a job-tracking card instead.",
    ],
  },
  on_error: {
    title: "On record error",
    body: [
      "What a permanently failed record does to the job.",
      'Skip: note it as failed and keep going — 29 succeed, 1 fails, the job ends "completed with errors".',
      "Fail job: the first permanent failure stops everything — use this only when partial results are useless (e.g. a billing run where every row must succeed).",
    ],
  },
  sink_mode: {
    title: "Sink mode",
    body: [
      "What happens with the results when all records are done.",
      "Post-chain: the nodes after the loop run automatically with the collected results (e.g. a Report Builder gets all 30 analyses and writes the report, then the main agent gives the final answer).",
      "None: nothing runs afterwards — results just sit in the job, available via the items list and CSV/JSON download. Use this when you only want the raw per-record data.",
    ],
  },
};

const getNextPythonNodeId = (selectedAgents = []) => {
  const nextIndex =
    selectedAgents.reduce((maxValue, agent) => {
      const match = String(agent?.id || "").match(/^py_calc_(\d+)$/);
      if (!match) {
        return maxValue;
      }
      return Math.max(maxValue, Number(match[1]) || 0);
    }, 0) + 1;

  return `py_calc_${nextIndex}`;
};


const AgentChainingDialog = ({ open, onClose, onSave, initialData, humanInLoopEnabled = false, currentAgentId = null }) => {
  const { isDarkMode } = useTheme();

  // Handler to open agent in new tab
  const handleAgentClick = (agentId, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    const url = `/agent-create?id=${agentId}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Helper component for detailed tooltips (matching AgentCreate.jsx pattern)
  const InfoTooltip = ({ title, content, children, placement = "top", maxWidth = 1500 }) => (
    <Tooltip
      title={
        <Box sx={{ 
          width: '100%',
          maxHeight: '400px',
          overflowY: 'auto',
          p: 1,
          boxSizing: "border-box",
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.5)',
            },
          },
        }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 1, color: "white" }}
          >
            {title}
          </Typography>
          {typeof content === "string" ? (
            <Typography variant="body2" sx={{ 
              color: "rgba(255,255,255,0.9)",
              wordWrap: "break-word",
              overflowWrap: "break-word"
            }}>
              {content}
            </Typography>
          ) : (
            content
          )}
        </Box>
      }
      placement={placement}
      arrow
      enterDelay={300}
      leaveDelay={200}
      sx={{
        "& .MuiTooltip-tooltip": {
          backgroundColor: "rgba(0, 0, 0, 0.9)",
          fontSize: "0.875rem",
          maxWidth: `${maxWidth}px !important`,
          padding: "12px 16px",
          wordWrap: "break-word",
          overflowWrap: "break-word",
          whiteSpace: "normal",
          boxSizing: "border-box",
        },
        "& .MuiTooltip-arrow": {
          color: "rgba(0, 0, 0, 0.9)",
        },
      }}
    >
      {children}
    </Tooltip>
  );

  // Help icon for a single loop config control (content comes from LOOP_FIELD_HELP).
  const LoopFieldHelp = ({ field }) => {
    const help = LOOP_FIELD_HELP[field];
    if (!help) return null;
    return (
      <InfoTooltip
        title={help.title}
        maxWidth={420}
        placement="top"
        content={
          <Box>
            {help.body.map((line, index) => (
              <Typography
                key={index}
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.75 }}
              >
                {line}
              </Typography>
            ))}
            {help.example && (
              <>
                <Typography variant="body2" sx={{ mb: 0.5, color: "#2196f3", fontWeight: 600 }}>
                  💡 Example
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                  {help.example}
                </Typography>
              </>
            )}
          </Box>
        }
      >
        <IconButton size="small" sx={{ color: "#e36250", mt: 0.25 }} aria-label={`${help.title} help`}>
          <HelpCircle size={14} strokeWidth={2} />
        </IconButton>
      </InfoTooltip>
    );
  };

  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? "dark" : "light",
      primary: {
        main: "#e36250 !important",
      },
      secondary: {
        main: "#8b5cf6",
      },
    },
  });

  const safeInitialData = initialData || {};
  const [formData, setFormData] = useState({
    selectedAgents: safeInitialData.selectedAgents || [],
    connections: safeInitialData.connections || [],
  });

  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [selectedWorkflowNodeId, setSelectedWorkflowNodeId] = useState(null);
  const [invalidConnectionMessage, setInvalidConnectionMessage] = useState("");
  const [availableAgents, setAvailableAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [agentsPage, setAgentsPage] = useState(1);
  const [agentsPageSize] = useState(20);
  const [loadingMoreAgents, setLoadingMoreAgents] = useState(false);
  const [hasMoreAgents, setHasMoreAgents] = useState(true);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const reactFlowWrapperRef = React.useRef(null);
  const loopSettingsPanelRef = React.useRef(null);

  // The loop settings panel renders below the canvas; bring it on screen when opened.
  const scrollLoopSettingsIntoView = useCallback(() => {
    requestAnimationFrame(() => {
      loopSettingsPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  }, []);

  const createNodeLabel = useCallback(
    (agent) => {
      const isPythonNode = isPythonExecStep(agent);
      const isLoopNode = isLoopStep(agent);

      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
            minWidth: isPythonNode || isLoopNode ? 220 : 120,
            maxWidth: isPythonNode || isLoopNode ? 240 : 180,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {isPythonNode ? (
              <Functions sx={{ fontSize: 18, color: isDarkMode ? "#7dd3fc" : "#0369a1" }} />
            ) : null}
            {isLoopNode ? (
              <LoopIcon sx={{ fontSize: 18, color: isDarkMode ? "#c4b5fd" : "#6d28d9" }} />
            ) : null}
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: isDarkMode ? "#ffffff" : "#1a1a1a",
                lineHeight: 1.2,
              }}
            >
              {isLoopNode ? `Loop: ${agent.name}` : agent.name}
            </Typography>
          </Box>

          {isPythonNode ? (
            <Typography
              variant="caption"
              sx={{
                lineHeight: 1.3,
                color: isDarkMode ? "rgba(255,255,255,0.82)" : "rgba(15,23,42,0.72)",
                whiteSpace: "normal",
              }}
            >
              Runs Python code from the previous agent. Previous node must emit {"{data, python_code}"} JSON.
            </Typography>
          ) : null}

          {isLoopNode ? (
            <Typography
              variant="caption"
              sx={{
                lineHeight: 1.3,
                color: isDarkMode ? "rgba(255,255,255,0.82)" : "rgba(15,23,42,0.72)",
                whiteSpace: "normal",
              }}
            >
              Runs this agent once per record. Previous step must output a JSON array.
            </Typography>
          ) : null}
        </Box>
      );
    },
    [isDarkMode]
  );

  const createFlowNode = useCallback(
    (agent, index = 0) => {
      const isPythonNode = isPythonExecStep(agent);
      const isLoopNode = isLoopStep(agent);

      if (isLoopNode) {
        return {
          id: agent.id,
          type: "default",
          position: agent.position || { x: 200 + index * 150, y: 150 },
          data: {
            label: createNodeLabel(agent),
          },
          style: {
            background: isDarkMode
              ? "linear-gradient(180deg, #2e1065 0%, #1e1b4b 100%)"
              : "linear-gradient(180deg, #f5f3ff 0%, #ede9fe 100%)",
            color: isDarkMode ? "#ffffff" : "#2e1065",
            border: `2px solid ${isDarkMode ? "#a78bfa" : "#7c3aed"}`,
            borderRadius: "12px",
            boxShadow: isDarkMode
              ? "0 12px 24px rgba(124, 58, 237, 0.18)"
              : "0 12px 24px rgba(139, 92, 246, 0.16)",
            padding: "4px",
          },
        };
      }

      return {
        id: agent.id,
        type: "default",
        position: agent.position || { x: 200 + index * 150, y: 150 },
        data: {
          label: createNodeLabel(agent),
        },
        style: isPythonNode
          ? {
              background: isDarkMode
                ? "linear-gradient(180deg, #082f49 0%, #0f172a 100%)"
                : "linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 100%)",
              color: isDarkMode ? "#ffffff" : "#082f49",
              border: `2px solid ${isDarkMode ? "#38bdf8" : "#0284c7"}`,
              borderRadius: "12px",
              boxShadow: isDarkMode
                ? "0 12px 24px rgba(2, 132, 199, 0.16)"
                : "0 12px 24px rgba(14, 165, 233, 0.16)",
              padding: "4px",
            }
          : {
              background: isDarkMode ? "#2a2a2a" : "#ffffff",
              color: isDarkMode ? "#ffffff" : "#1a1a1a",
              border: "2px solid #e36250",
              borderRadius: "8px",
            },
      };
    },
    [createNodeLabel, isDarkMode]
  );

  const buildEdgeConfig = useCallback((source, target) => {
    const edgeId = `${source}-${target}-${Date.now()}`;

    return {
      reactFlowEdge: {
        id: edgeId,
        source,
        target,
        type: "custom",
        style: {
          stroke: "#e36250",
          strokeWidth: 2,
        },
        markerEnd: {
          type: "arrowclosed",
          color: "#e36250",
        },
        animated: true,
      },
      savedConnection: {
        id: edgeId,
        source,
        target,
        type: "custom",
      },
    };
  }, []);

  const selectedWorkflowNode = useMemo(
    () =>
      formData.selectedAgents.find((agent) => agent.id === selectedWorkflowNodeId) ||
      null,
    [formData.selectedAgents, selectedWorkflowNodeId]
  );

  const validateConnections = useCallback((connections, selectedAgents = []) => {
    // Check for circular connections in saved data
    const hasCycle = (edges) => {
      const visited = new Set();
      const recStack = new Set();

      const dfs = (node) => {
        if (recStack.has(node)) return true; // Cycle found
        if (visited.has(node)) return false;

        visited.add(node);
        recStack.add(node);

        const outgoing = edges
          .filter(edge => edge.source === node)
          .map(edge => edge.target);

        for (const neighbor of outgoing) {
          if (dfs(neighbor)) return true;
        }

        recStack.delete(node);
        return false;
      };

      const allNodes = new Set();
      edges.forEach(edge => {
        allNodes.add(edge.source);
        allNodes.add(edge.target);
      });

      for (const node of allNodes) {
        if (!visited.has(node)) {
          if (dfs(node)) return true;
        }
      }
      return false;
    };

    // Check for nodes with multiple incoming connections
    const hasMultipleIncoming = (edges) => {
      const incomingCount = new Map();
      edges.forEach(edge => {
        const count = incomingCount.get(edge.target) || 0;
        incomingCount.set(edge.target, count + 1);
      });
      // Check if any node has more than one incoming connection
      for (const count of incomingCount.values()) {
        if (count > 1) return true;
      }
      return false;
    };

    if (hasCycle(connections) || hasMultipleIncoming(connections)) {
      return false;
    }

    const agentsById = new Map(selectedAgents.map((agent) => [agent.id, agent]));

    for (const edge of connections) {
      const sourceAgent = agentsById.get(edge.source);
      const targetAgent = agentsById.get(edge.target);

      if (isPythonExecStep(sourceAgent) && isPythonExecStep(targetAgent)) {
        return false;
      }
    }

    for (const agent of selectedAgents) {
      if (!isPythonExecStep(agent)) {
        continue;
      }

      const incomingEdges = connections.filter((edge) => edge.target === agent.id);
      const outgoingEdges = connections.filter((edge) => edge.source === agent.id);
      const sourceAgent = agentsById.get(incomingEdges[0]?.source);

      if (
        incomingEdges.length !== 1 ||
        outgoingEdges.length > 1 ||
        !sourceAgent ||
        isPythonExecStep(sourceAgent)
      ) {
        return false;
      }
    }

    const rootAgents = selectedAgents.filter(
      (agent) => !connections.some((connection) => connection.target === agent.id)
    );

    return !rootAgents.some((agent) => isPythonExecStep(agent));
  }, []);

  const initializeFlow = useCallback((selectedAgents = [], savedConnections = []) => {
    // Add selected agents to the flow, preserving their positions if they exist
    const agentNodes = selectedAgents.map((agent, index) =>
      createFlowNode(agent, index)
    );

    setNodes(agentNodes);
    
    // Restore saved connections or auto-connect if no saved connections
    if (savedConnections && savedConnections.length > 0) {
      // Validate connections before restoring
      if (validateConnections(savedConnections, selectedAgents)) {
        const restoredEdges = savedConnections.map(conn => ({
          id: conn.id,
          source: conn.source,
          target: conn.target,
          type: "custom",
          style: {
            stroke: "#e36250",
            strokeWidth: 2,
          },
          markerEnd: {
            type: "arrowclosed",
            color: "#e36250",
          },
          animated: true,
        }));
        setEdges(restoredEdges);
        // Update formData with restored connections
        setFormData((prev) => ({
          ...prev,
          connections: savedConnections
        }));
      } else {
        setInvalidConnectionMessage("Invalid connections detected (circular dependencies or nodes with multiple inputs). Invalid connections have been removed.");
        setTimeout(() => setInvalidConnectionMessage(""), 5000);
        // Clear invalid connections - let user create connections manually
        setEdges([]);
        setFormData((prev) => ({
          ...prev,
          connections: []
        }));
      }
    } else {
      // No saved connections - start with no connections, let user create them manually
      setEdges([]);
      setFormData((prev) => ({
        ...prev,
        connections: []
      }));
    }
  }, [createFlowNode, setNodes, setEdges, validateConnections]);

  // Fetch available agents from API
  const fetchAvailableAgents = useCallback(async (pageNum = 1, append = false) => {
    if (append) {
      setLoadingMoreAgents(true);
    } else {
      setLoadingAgents(true);
    }
    
    try {
      const response = await agentService.getAgents(pageNum, agentsPageSize);
      const agentsData = response.agents || [];
      
      // Transform the API data to match the expected format
      const transformedAgents = agentsData.map(agent => ({
        id: agent.id.toString(),
        name: agent.name,
        type: agent.name || 'general',
        description: agent.description || '',
        agent_role: agent.agent_role || '',
        agent_goal: agent.agent_goal || '',
        llm_provider: agent.llm_provider?.name || '',
        llm_model: agent.llm_model?.name || '',
        features: agent.features || {},
        temperature: agent.temperature || '',
        top_p: agent.top_p || '',
        variables: agent.variables || ''
      }));
      
      if (append) {
        setAvailableAgents(prev => [...prev, ...transformedAgents]);
      } else {
        setAvailableAgents(transformedAgents);
      }
      
      setHasMoreAgents(pageNum < (response.total_pages || 0));
    } catch (error) {
      if (!append) {
        setAvailableAgents([]);
      }
    } finally {
      setLoadingAgents(false);
      setLoadingMoreAgents(false);
    }
  }, [agentsPageSize]);

  // The agent being edited must never appear in its own chain (self-reference is invalid).
  const currentAgentIdStr = currentAgentId != null ? String(currentAgentId) : null;

  const handleAgentSelect = (agentId) => {
    if (currentAgentIdStr && agentId === currentAgentIdStr) {
      setInvalidConnectionMessage("An agent cannot be added to its own chain.");
      setTimeout(() => setInvalidConnectionMessage(""), 5000);
      setSelectedAgentId("");
      return;
    }

    const agent = availableAgents.find((a) => a.id === agentId);

    if (agent && !formData.selectedAgents.find((a) => a.id === agentId)) {
      const newAgent = {
        ...agent,
        position: { x: Math.random() * 300 + 200, y: Math.random() * 200 + 150 },
        description: "",
        variables: agent.variables || "",
      };

      setFormData((prev) => ({
        ...prev,
        selectedAgents: [...prev.selectedAgents, newAgent],
      }));

      // Add node to flow
      const newNode = createFlowNode(newAgent);
      
      setNodes((nds) => {
        // Just add the new node without any connections - let user create connections manually
        return [...nds, newNode];
      });
      setSelectedWorkflowNodeId(agentId);
    }

    // Reset the select value after selection
    setSelectedAgentId("");
  };

  /**
   * Mark/unmark a workflow agent as the chain's loop node.
   * Restyles the canvas node in place and opens the loop settings panel when enabling.
   */
  const handleToggleLoopNode = (agentId, enabled) => {
    const agent = formData.selectedAgents.find((a) => a.id === agentId);
    if (!agent || isPythonExecStep(agent)) {
      return;
    }

    if (enabled) {
      const existingLoop = formData.selectedAgents.find(
        (a) => isLoopStep(a) && a.id !== agentId
      );
      if (existingLoop) {
        setInvalidConnectionMessage(
          `Only one loop node is allowed per chain. "${existingLoop.name}" is already the loop node.`
        );
        setTimeout(() => setInvalidConnectionMessage(""), 5000);
        return;
      }
    }

    let updatedAgent;
    if (enabled) {
      updatedAgent = {
        ...agent,
        type: LOOP_EXEC_TYPE,
        executionKind: LOOP_EXEC_TYPE,
        configJson: getLoopConfigJsonString(agent) || buildLoopConfigJson(),
      };
    } else {
      const {
        executionKind,
        execution_kind: executionKindSnake,
        configJson,
        config_json: configJsonSnake,
        ...rest
      } = agent;
      updatedAgent = { ...rest, type: agent.name || "general" };
    }

    setFormData((prev) => ({
      ...prev,
      selectedAgents: prev.selectedAgents.map((a) =>
        a.id === agentId ? updatedAgent : a
      ),
    }));

    // Re-render the canvas node with the new style, keeping its current position.
    setNodes((nds) =>
      nds.map((node) =>
        node.id === agentId
          ? createFlowNode({ ...updatedAgent, position: node.position })
          : node
      )
    );

    if (enabled) {
      setSelectedWorkflowNodeId(agentId);
      scrollLoopSettingsIntoView();
    }
  };

  const handleLoopConfigChange = (agentId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      selectedAgents: prev.selectedAgents.map((agent) =>
        agent.id === agentId && isLoopStep(agent)
          ? {
              ...agent,
              configJson: buildLoopConfigJson({
                ...readLoopConfigJson(getLoopConfigJsonString(agent)),
                [field]: value,
              }),
            }
          : agent
      ),
    }));
  };

  const handlePaletteDragStart = useCallback((event, nodeType) => {
    event.dataTransfer.setData("application/reactflow", nodeType);
    event.dataTransfer.effectAllowed = "move";
  }, []);

  const getPythonDropSource = useCallback(() => {
    const selectedSource = formData.selectedAgents.find(
      (agent) => agent.id === selectedWorkflowNodeId
    );

    if (selectedSource && !isPythonExecStep(selectedSource)) {
      return selectedSource;
    }

    const terminalAgents = formData.selectedAgents.filter(
      (agent) =>
        !isPythonExecStep(agent) &&
        !edges.some((edge) => edge.source === agent.id)
    );

    if (terminalAgents.length === 1) {
      return terminalAgents[0];
    }

    return null;
  }, [edges, formData.selectedAgents, selectedWorkflowNodeId]);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const droppedType = event.dataTransfer.getData("application/reactflow");
      if (droppedType !== PYTHON_EXEC_TYPE) {
        return;
      }

      if (!reactFlowInstance || !reactFlowWrapperRef.current) {
        return;
      }

      const sourceAgent = getPythonDropSource();
      if (!sourceAgent) {
        setInvalidConnectionMessage(
          "Python Calculator must be dropped after an agent node. Select an agent node first, or leave exactly one terminal agent in the chain."
        );
        setTimeout(() => setInvalidConnectionMessage(""), 5000);
        return;
      }

      const bounds = reactFlowWrapperRef.current.getBoundingClientRect();
      const position = reactFlowInstance.project({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      });

      const newNodeId = getNextPythonNodeId(formData.selectedAgents);
      const newPythonNode = {
        id: newNodeId,
        name: PYTHON_NODE_NAME,
        type: PYTHON_EXEC_TYPE,
        position,
        description: "",
        connectsTo: null,
        connectedFrom: sourceAgent.id,
        executionKind: PYTHON_EXEC_TYPE,
        configJson: buildPythonConfigJson(),
        outgoingConnections: [],
        incomingConnections: [],
      };

      const { reactFlowEdge, savedConnection } = buildEdgeConfig(
        sourceAgent.id,
        newNodeId
      );

      setFormData((prev) => ({
        ...prev,
        selectedAgents: [...prev.selectedAgents, newPythonNode],
        connections: [...prev.connections, savedConnection],
      }));

      setNodes((nds) => [...nds, createFlowNode(newPythonNode)]);
      setEdges((eds) => addEdge(reactFlowEdge, eds));
      setSelectedWorkflowNodeId(newNodeId);
      setInvalidConnectionMessage("");
    },
    [
      buildEdgeConfig,
      createFlowNode,
      formData.selectedAgents,
      getPythonDropSource,
      reactFlowInstance,
      setEdges,
      setNodes,
    ]
  );

  useEffect(() => {
    if (open) {
      const initialSelectedAgents = safeInitialData.selectedAgents || [];
      const initialConnections = safeInitialData.connections || [];
      setFormData({
        selectedAgents: initialSelectedAgents,
        connections: initialConnections,
      });
      initializeFlow(initialSelectedAgents, initialConnections);
      setSelectedWorkflowNodeId(null);
      
      // Reset pagination and fetch available agents when dialog opens
      setAgentsPage(1);
      setAvailableAgents([]);
      fetchAvailableAgents(1, false);
    }
  }, [open, initialData, safeInitialData.selectedAgents, safeInitialData.connections, initializeFlow, fetchAvailableAgents]);

  // Throttle scroll handler to prevent excessive API calls
  const scrollTimeoutRef = React.useRef(null);
  const isFetchingRef = React.useRef(false);

  // Handle scroll in dropdown menu to load more agents
  const handleMenuScroll = React.useCallback((e) => {
    if (!e || !e.currentTarget) {
      return;
    }

    const menuPaper = e.currentTarget;
    if (!menuPaper) {
      return;
    }

    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Throttle the scroll handler - capture values immediately
    scrollTimeoutRef.current = setTimeout(() => {
      // Re-check if element still exists
      if (!menuPaper || menuPaper.scrollTop === undefined) {
        return;
      }

      const scrollTop = menuPaper.scrollTop;
      const scrollHeight = menuPaper.scrollHeight;
      const clientHeight = menuPaper.clientHeight;
      
      // Check if user has scrolled near the bottom (within 200px to trigger earlier)
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 200;
      
      
      if (isNearBottom && hasMoreAgents && !loadingMoreAgents && !loadingAgents && !isFetchingRef.current) {
        isFetchingRef.current = true;
        const nextPage = agentsPage + 1;
        setAgentsPage(nextPage);
        fetchAvailableAgents(nextPage, true).finally(() => {
          isFetchingRef.current = false;
        });
      }
    }, 150);
  }, [hasMoreAgents, loadingMoreAgents, loadingAgents, agentsPage, fetchAvailableAgents]);


  const handleRemoveAgent = (agentId) => {
    // Update formData with current node positions before removing
    setFormData((prev) => {
      const updatedAgents = prev.selectedAgents.map(agent => {
        const node = nodes.find(n => n.id === agent.id);
        return {
          ...agent,
          position: node ? node.position : agent.position
        };
      }).filter((a) => a.id !== agentId);
      
      // Remove connections involving this agent
      const updatedConnections = prev.connections.filter(conn => 
        conn.source !== agentId && conn.target !== agentId
      );
      
      return {
        ...prev,
        selectedAgents: updatedAgents,
        connections: updatedConnections,
      };
    });

    // Remove node - connections are already removed in formData update above
    setNodes((nds) => {
      return nds.filter((node) => node.id !== agentId);
    });
    setEdges((eds) =>
      eds.filter((edge) => edge.source !== agentId && edge.target !== agentId)
    );
    if (selectedWorkflowNodeId === agentId) {
      setSelectedWorkflowNodeId(null);
    }
  };

  const handleAgentDescriptionChange = (agentId, description) => {
    setFormData((prev) => ({
      ...prev,
      selectedAgents: prev.selectedAgents.map((agent) =>
        agent.id === agentId ? { ...agent, description } : agent
      ),
    }));
  };

  const handlePythonTimeoutChange = (agentId, nextValue) => {
    setFormData((prev) => ({
      ...prev,
      selectedAgents: prev.selectedAgents.map((agent) =>
        agent.id === agentId
          ? {
              ...agent,
              configJson: buildPythonConfigJson(
                nextValue,
                readPythonConfigJson(agent.configJson).config.load_files
              ),
            }
          : agent
      ),
    }));
  };

  const handlePythonLoadFilesChange = (agentId, loadFiles) => {
    setFormData((prev) => ({
      ...prev,
      selectedAgents: prev.selectedAgents.map((agent) =>
        agent.id === agentId
          ? {
              ...agent,
              configJson: buildPythonConfigJson(
                readPythonConfigJson(agent.configJson).config.timeout_seconds,
                loadFiles
              ),
            }
          : agent
      ),
    }));
  };

  const onConnect = useCallback(
    (params) => {
      const sourceNode = formData.selectedAgents.find((agent) => agent.id === params.source);
      const targetNode = formData.selectedAgents.find((agent) => agent.id === params.target);

      if (!sourceNode || !targetNode) {
        return;
      }

      // Prevent circular connections
      const wouldCreateCycle = (source, target, currentEdges) => {
        const visited = new Set();
        const stack = [target];

        while (stack.length > 0) {
          const current = stack.pop();
          if (current === source) {
            return true; // Cycle detected
          }
          if (visited.has(current)) {
            continue;
          }
          visited.add(current);

          // Find all nodes that current connects to
          const outgoing = currentEdges
            .filter(edge => edge.source === current)
            .map(edge => edge.target);
          stack.push(...outgoing);
        }
        return false;
      };

      // Check if this connection would create a cycle
      if (wouldCreateCycle(params.source, params.target, edges)) {
        setInvalidConnectionMessage("Cannot create circular connection - would create infinite loop");
        setTimeout(() => setInvalidConnectionMessage(""), 3000);
        return; // Prevent the connection
      }

      if (isPythonExecStep(targetNode) && isPythonExecStep(sourceNode)) {
        setInvalidConnectionMessage("Python Calculator nodes cannot read from another Python Calculator node.");
        setTimeout(() => setInvalidConnectionMessage(""), 4000);
        return;
      }

      if (isPythonExecStep(sourceNode)) {
        const existingOutgoingConnection = edges.find(
          (edge) => edge.source === params.source
        );

        if (existingOutgoingConnection) {
          setInvalidConnectionMessage("Python Calculator nodes can only connect to one next step.");
          setTimeout(() => setInvalidConnectionMessage(""), 4000);
          return;
        }
      }

      // Check if this exact connection already exists
      const duplicateConnection = edges.find(
        edge => edge.source === params.source && edge.target === params.target
      );
      if (duplicateConnection) {
        setInvalidConnectionMessage("This connection already exists");
        setTimeout(() => setInvalidConnectionMessage(""), 3000);
        return;
      }

      // Check if target node already has an incoming connection (prevent multiple inputs)
      const existingIncomingConnection = edges.find(
        edge => edge.target === params.target
      );
      if (existingIncomingConnection) {
        const existingSourceNode = formData.selectedAgents.find(
          (agent) => agent.id === existingIncomingConnection.source
        );
        const existingTargetNode = formData.selectedAgents.find(
          (agent) => agent.id === params.target
        );
        setInvalidConnectionMessage(
          `Node "${existingTargetNode?.name || params.target}" already receives input from "${existingSourceNode?.name || existingIncomingConnection.source}". Each node can only receive input from one node.`
        );
        setTimeout(() => setInvalidConnectionMessage(""), 4000);
        return;
      }

      // Clear any previous invalid connection message
      setInvalidConnectionMessage("");

      const { reactFlowEdge, savedConnection } = buildEdgeConfig(
        params.source,
        params.target
      );

      setEdges((eds) => addEdge(reactFlowEdge, eds));

      // Update formData with the new connection
      setFormData((prev) => ({
        ...prev,
        connections: [...prev.connections, savedConnection]
      }));
    },
    [buildEdgeConfig, edges, formData.selectedAgents, setEdges, setFormData]
  );

  // Handle edge deletion (when user presses Delete/Backspace key or clicks delete button)
  const onEdgesDelete = useCallback(
    (edgesToDelete) => {
      // Update formData to remove deleted connections
      setFormData((prev) => {
        const deletedEdgeIds = new Set(edgesToDelete.map(edge => edge.id));
        return {
          ...prev,
          connections: prev.connections.filter(conn => !deletedEdgeIds.has(conn.id))
        };
      });
    },
    [setFormData]
  );

  // Define edgeTypes with custom edge - memoized to prevent recreation on every render
  const edgeTypes = useMemo(() => {
    const CustomEdge = ({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style = {}, markerEnd }) => {
      const { deleteElements } = useReactFlow();
      const [edgePath, labelX, labelY] = getBezierPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
      });

      const handleDelete = (e) => {
        e.stopPropagation();
        const edgeToDelete = { id };
        deleteElements({ edges: [edgeToDelete] });
        // Also call onEdgesDelete to update formData
        onEdgesDelete([edgeToDelete]);
      };

      return (
        <>
          <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
          <EdgeLabelRenderer>
            <div
              style={{
                position: 'absolute',
                transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
                pointerEvents: 'all',
              }}
              className="nodrag nopan"
            >
              <IconButton
                size="small"
                onClick={handleDelete}
                sx={{
                  backgroundColor: '#e36250',
                  color: 'white',
                  width: 15,
                  height: 15,
                  padding: 0,
                  '&:hover': {
                    backgroundColor: '#c54d3d',
                  },
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                }}
                title="Delete connection"
              >
                <Delete sx={{ fontSize: 9 }} />
              </IconButton>
            </div>
          </EdgeLabelRenderer>
        </>
      );
    };

    return {
      custom: CustomEdge,
    };
  }, [onEdgesDelete]);

  const handleSave = () => {
    // Determine chaining strategy based on connections
    // If any node has multiple outgoing connections, it's parallel
    // Otherwise, it's sequential
    const determineStrategy = () => {
      const outgoingCounts = new Map();
      edges.forEach(edge => {
        const count = outgoingCounts.get(edge.source) || 0;
        outgoingCounts.set(edge.source, count + 1);
      });

      // Check if any node has more than one outgoing connection
      for (const count of outgoingCounts.values()) {
        if (count > 1) {
          return 'parallel';
        }
      }
      return 'sequential';
    };

    const chainingStrategy = determineStrategy();

    const serializedWorkflowNodes = formData.selectedAgents.map((agent) => {
      const node = nodes.find((n) => n.id === agent.id);
      const agentPosition = node ? node.position : agent.position;

      const outgoingEdges = edges.filter((edge) => edge.source === agent.id);
      const outgoingConnections = outgoingEdges.map((edge) => ({
        id: edge.id,
        target: edge.target,
        type: edge.type || "smoothstep",
      }));

      const incomingEdges = edges.filter((edge) => edge.target === agent.id);
      const incomingConnections = incomingEdges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        type: edge.type || "smoothstep",
      }));

      if (isPythonExecStep(agent)) {
        const parsedConfig = readPythonConfigJson(agent.configJson);

        return {
          id: agent.id,
          name: agent.name || PYTHON_NODE_NAME,
          type: PYTHON_EXEC_TYPE,
          position: agentPosition,
          description: agent.description || "",
          connectsTo: outgoingConnections[0]?.target || null,
          connectedFrom: incomingConnections[0]?.source || null,
          executionKind: PYTHON_EXEC_TYPE,
          configJson: parsedConfig.isValid
            ? buildPythonConfigJson(
                parsedConfig.config.timeout_seconds,
                parsedConfig.config.load_files
              )
            : agent.configJson,
          outgoingConnections,
          incomingConnections,
        };
      }

      if (isLoopStep(agent)) {
        return {
          id: agent.id,
          name: agent.name,
          type: LOOP_EXEC_TYPE,
          position: agentPosition,
          description: agent.description || "",
          variables: agent.variables || "",
          executionKind: LOOP_EXEC_TYPE,
          configJson: buildLoopConfigJson(
            readLoopConfigJson(getLoopConfigJsonString(agent))
          ),
          connectsTo: outgoingConnections.map((conn) => conn.target),
          connectedFrom: incomingConnections.map((conn) => conn.source),
          outgoingConnections,
          incomingConnections,
        };
      }

      return {
        id: agent.id,
        name: agent.name,
        type: agent.type,
        position: agentPosition,
        description: agent.description || "",
        variables: agent.variables || "",
        connectsTo: outgoingConnections.map((conn) => conn.target),
        connectedFrom: incomingConnections.map((conn) => conn.source),
        outgoingConnections,
        incomingConnections,
      };
    });

    const pythonExecNodes = serializedWorkflowNodes.filter((agent) =>
      isPythonExecStep(agent)
    );

    for (const pythonNode of pythonExecNodes) {
      const parsedConfig = readPythonConfigJson(pythonNode.configJson);
      const incomingSourceAgent = serializedWorkflowNodes.find(
        (agent) => agent.id === pythonNode.connectedFrom
      );

      if (!parsedConfig.isValid) {
        setInvalidConnectionMessage(
          `Python Calculator "${pythonNode.name}" must have a valid configJson JSON string.`
        );
        return;
      }

      if (pythonNode.incomingConnections.length !== 1 || !pythonNode.connectedFrom) {
        setInvalidConnectionMessage(
          `Python Calculator "${pythonNode.name}" must have exactly one incoming agent connection.`
        );
        return;
      }

      if (!incomingSourceAgent || isPythonExecStep(incomingSourceAgent)) {
        setInvalidConnectionMessage(
          `Python Calculator "${pythonNode.name}" must read from an agent node.`
        );
        return;
      }

      if (pythonNode.outgoingConnections.length > 1) {
        setInvalidConnectionMessage(
          `Python Calculator "${pythonNode.name}" can only connect to one next step.`
        );
        return;
      }
    }

    const workflowRoots = serializedWorkflowNodes.filter(
      (agent) => (agent.incomingConnections || []).length === 0
    );

    if (workflowRoots.some((agent) => isPythonExecStep(agent))) {
      setInvalidConnectionMessage("Workflow cannot start with a Python Calculator node.");
      return;
    }

    if (
      currentAgentIdStr &&
      serializedWorkflowNodes.some((agent) => agent.id === currentAgentIdStr)
    ) {
      setInvalidConnectionMessage(
        "This chain contains the agent you are editing. An agent cannot be part of its own chain — remove it before saving."
      );
      return;
    }

    // Loop node validation (v1 backend limitations)
    const loopNodes = serializedWorkflowNodes.filter((agent) => isLoopStep(agent));

    if (loopNodes.length > 1) {
      setInvalidConnectionMessage("A chain may contain exactly one loop node.");
      return;
    }

    if (loopNodes.length === 1) {
      const loopNode = loopNodes[0];

      if (!Number.isInteger(Number(loopNode.id))) {
        setInvalidConnectionMessage(
          `Loop node "${loopNode.name}" must reference a valid agent.`
        );
        return;
      }

      if ((loopNode.incomingConnections || []).length === 0) {
        setInvalidConnectionMessage(
          "A loop node requires a previous step that outputs a JSON array of records."
        );
        return;
      }

      // The loop node cannot share a fan-out level with sibling nodes (no parallel branch).
      const loopSourceId = loopNode.incomingConnections[0]?.source;
      const siblingEdges = edges.filter(
        (edge) => edge.source === loopSourceId && edge.target !== loopNode.id
      );
      if (siblingEdges.length > 0) {
        setInvalidConnectionMessage("A loop node cannot be part of a parallel group.");
        return;
      }
    }

    // Create a combined structure for database storage
    const agentChainData = {
      // Basic chain info
      name: "Agent Chain", // You can make this configurable
      description: "Generated agent chain",

      // Maintain backward compatibility with selectedAgents
      selectedAgents: serializedWorkflowNodes,

      // Also include the combined structure for database storage
      agents: serializedWorkflowNodes,

      // Save connections as a flat array for easy restoration
      connections: edges.map(edge => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        type: edge.type || "smoothstep"
      })),

      // Automatically determined chaining strategy
      chainingStrategy,

      // Note: chainedVariables are now fetched dynamically via API in AgentCreate.jsx
      // They are no longer collected or sent from this dialog

      // Chain metadata
      totalAgents: formData.selectedAgents.length,
      totalConnections: edges.length,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    onSave(agentChainData);
    onClose(agentChainData);
  };

  const handleCancel = () => {
    onClose(formData);
  };


  return (
    <ThemeProvider theme={muiTheme}>
      <Dialog
        open={open}
        onClose={() => onClose(formData)}
        maxWidth={false}
        fullWidth
        scroll="paper"
        className={styles.dialog}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(24, 24, 27, 0.4)",
              backdropFilter: "blur(2px)",
            },
          },
        }}
        PaperProps={{
          className: styles.dialogPaper,
        }}
      >
        <DialogTitle className={styles.dialogTitle} component="div">
          <Box className={styles.titleRow}>
            <Typography component="h3" variant="h6" className={styles.title}>
              Agent Chaining Configuration
            </Typography>
            <InfoTooltip
              title="Agent Flow Types"
              maxWidth={400}
              placement="right"
              content={
                <Box>
                  <Box sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <AccountTree sx={{ fontSize: 16, mr: 0.5, color: "rgba(255,255,255,0.9)" }} />
                      <Typography variant="caption" sx={{ fontWeight: 600, color: "rgba(255,255,255,0.9)" }}>
                        Sequential Flow
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.4, color: "rgba(255,255,255,0.9)" }}>
                      Agents execute one after another. Output of one becomes input for the next.
                      <br />
                      Example: Agent A → Agent B → Agent C
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <CallSplit sx={{ fontSize: 16, mr: 0.5, color: "rgba(255,255,255,0.9)" }} />
                      <Typography variant="caption" sx={{ fontWeight: 600, color: "rgba(255,255,255,0.9)" }}>
                        Parallel Flow
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.4, color: "rgba(255,255,255,0.9)" }}>
                      Multiple agents execute simultaneously from one source. Each node can only receive input from one node.
                      <br />
                      Example: Agent A → Agent B, C, D (all run in parallel)
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <LoopIcon sx={{ fontSize: 16, mr: 0.5, color: "rgba(255,255,255,0.9)" }} />
                      <Typography variant="caption" sx={{ fontWeight: 600, color: "rgba(255,255,255,0.9)" }}>
                        Loop / For Each
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.4, color: "rgba(255,255,255,0.9)" }}>
                      Turn on "Run for each record" on a workflow node to run that agent once per
                      record of the previous step's JSON array output. Small datasets run inline;
                      large ones run as a background batch job you can track on the Batch Jobs page.
                      <br />
                      Example: Sheet Fetcher → Loop: Scraper (per row) → Report Builder
                    </Typography>
                  </Box>

                  <Box sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.2)', pt: 1, mt: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: "rgba(255,255,255,0.9)" }}>
                      Tips:
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.4, color: "rgba(255,255,255,0.9)" }}>
                      • Draw connections to create workflows
                      <br />
                      • Click the delete icon on any connection to remove it
                      <br />
                      • Circular connections prevented
                      <br />
                      • Each node can only receive input from one node
                      <br />
                      • Combine flows for complex workflows
                    </Typography>
                  </Box>
                </Box>
              }
            >
              <IconButton size="small" className={styles.headerHelpIcon} aria-label="Agent flow types help">
                <HelpCircle size={16} strokeWidth={2} />
              </IconButton>
            </InfoTooltip>
          </Box>
          <IconButton
            onClick={() => onClose(formData)}
            size="small"
            className={styles.closeButton}
            aria-label="Close"
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent className={styles.dialogContent}>
          <Box className={styles.formContainer}>
            {/* Agent Selection */}
            <Box className={styles.section}>
              <Typography variant="body2" className={styles.sectionLabel}>
                Select Agents
              </Typography>
              <FormControl fullWidth size="small" className={styles.inputField}>
                <InputLabel>Add agents to chain</InputLabel>
                <Select
                  value={selectedAgentId}
                  onChange={(e) => {
                    handleAgentSelect(e.target.value);
                  }}
                  label="Add agents to chain"
                  disabled={loadingAgents}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        overflow: 'auto',
                      },
                      onScroll: handleMenuScroll,
                      sx: {
                        maxWidth: 'calc(1200px - 48px)',
                        width: 'auto',
                        '& .MuiMenuItem-root': {
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          maxWidth: '100%',
                          cursor: 'pointer',
                          '& .MuiBox-root': {
                            maxWidth: '100%',
                            wordWrap: 'break-word',
                            pointerEvents: 'none',
                          },
                          '& .MuiTypography-root': {
                            pointerEvents: 'none',
                          },
                          '& .MuiChip-root': {
                            pointerEvents: 'none',
                          },
                        },
                      },
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    disableScrollLock: true,
                  }}
                  endAdornment={
                    loadingAgents ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                        <CircularProgress size={16} />
                      </Box>
                    ) : null
                  }
                >
                  {loadingAgents ? (
                    <MenuItem disabled>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      Loading agents...
                    </MenuItem>
                  ) : availableAgents.length === 0 ? (
                    <MenuItem disabled>
                      No agents available
                    </MenuItem>
                  ) : (
                    <>
                      {availableAgents
                        .filter(
                          (agent) =>
                            agent.id !== currentAgentIdStr &&
                            !formData.selectedAgents.find((a) => a.id === agent.id)
                        )
                        .map((agent) => (
                          <MenuItem
                            key={agent.id}
                            value={agent.id}
                            onClick={() => {
                              handleAgentSelect(agent.id);
                            }}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: 'action.hover',
                              }
                            }}
                          >
                            <Box sx={{ width: '100%', py: 0.5, pointerEvents: 'none' }}>
                              {/* Agent Name */}
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                                {agent.name}
                              </Typography>

                              {/* Agent Role */}
                              {agent.agent_role && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                  <strong>Role:</strong> {agent.agent_role}
                                </Typography>
                              )}

                              {/* LLM Info */}
                              <Box sx={{ display: 'flex', gap: 2, mb: 0.5 }}>
                                {agent.llm_provider && (
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    <strong>Provider:</strong> {agent.llm_provider}
                                  </Typography>
                                )}
                                {agent.llm_model && (
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    <strong>Model:</strong> {agent.llm_model}
                                  </Typography>
                                )}
                              </Box>

                              {/* Features */}
                              {agent.features && Object.keys(agent.features).length > 0 && (
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                                  {Object.entries(agent.features)
                                    .filter(([key, value]) => value === true)
                                    .map(([key, value]) => (
                                      <Chip
                                        key={key}
                                        label={key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        size="small"
                                        sx={{
                                          height: 16,
                                          fontSize: '0.65rem',
                                          backgroundColor: '#e36250',
                                          color: 'white',
                                          fontWeight: 500,
                                          '& .MuiChip-label': {
                                            px: 0.5
                                          }
                                        }}
                                      />
                                    ))}
                                </Box>
                              )}

                              {/* Description */}
                              {agent.description && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'text.secondary',
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '100%'
                                  }}
                                >
                                  {agent.description}
                                </Typography>
                              )}
                            </Box>
                          </MenuItem>
                        ))}
                      {loadingMoreAgents && (
                        <MenuItem disabled>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            gap: 1, 
                            width: '100%', 
                            py: 2 
                          }}>
                            <CircularProgress size={16} />
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              Loading more agents...
                            </Typography>
                          </Box>
                        </MenuItem>
                      )}
                    </>
                  )}
                </Select>
              </FormControl>

              {humanInLoopEnabled && formData.selectedAgents.some((agent) => isLoopStep(agent)) && (
                <Box sx={{
                  p: 1,
                  mt: 1,
                  bgcolor: "warning.light",
                  color: "warning.contrastText",
                  borderRadius: 1,
                  fontSize: "0.875rem"
                }}>
                  Loop nodes are not supported in human-in-the-loop workflows yet. Disable
                  Human-in-Loop or remove the loop node before running this agent.
                </Box>
              )}

              {/* Selected Agents */}
              {formData.selectedAgents.length > 0 && (
                <Box className={styles.selectedAgents}>
                  <Typography variant="body2" className={styles.subLabel}>
                    Workflow Nodes:
                  </Typography>
                  <Stack spacing={2}>
                    {formData.selectedAgents.map((agent) => (
                      <Box key={agent.id} className={styles.agentItem}>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
                          <Chip
                            icon={
                              isPythonExecStep(agent) ? (
                                <Functions />
                              ) : isLoopStep(agent) ? (
                                <LoopIcon sx={{ color: "#ffffff !important" }} />
                              ) : undefined
                            }
                            label={
                              isPythonExecStep(agent) ? (
                                agent.name
                              ) : (
                                <span
                                  onClick={(e) => handleAgentClick(agent.id, e)}
                                  style={{
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    textDecorationColor: isLoopStep(agent)
                                      ? 'rgba(255, 255, 255, 0.5)'
                                      : 'rgba(227, 98, 80, 0.5)',
                                  }}
                                  title="Click to open agent in new tab"
                                >
                                  {agent.name}
                                </span>
                              )
                            }
                            onDelete={() => handleRemoveAgent(agent.id)}
                            deleteIcon={<Delete />}
                            className={styles.agentChip}
                            onClick={(e) => {
                              // Only handle click if not clicking on delete icon
                              if (e.target.closest('.MuiChip-deleteIcon')) {
                                return;
                              }
                              setSelectedWorkflowNodeId(agent.id);
                              if (!isPythonExecStep(agent) && !isLoopStep(agent)) {
                                handleAgentClick(agent.id, e);
                              }
                            }}
                            sx={{
                              backgroundColor: isPythonExecStep(agent)
                                ? "#0369a1 !important"
                                : isLoopStep(agent)
                                ? "#6d28d9 !important"
                                : undefined,
                              color: isPythonExecStep(agent) || isLoopStep(agent) ? "#ffffff" : undefined,
                              '&:hover': {
                                backgroundColor: isPythonExecStep(agent)
                                  ? "#075985 !important"
                                  : isLoopStep(agent)
                                  ? "#5b21b6 !important"
                                  : 'action.hover',
                              }
                            }}
                          />
                          {!isPythonExecStep(agent) && (
                            <FormControlLabel
                              sx={{ ml: 0, mr: 0 }}
                              control={
                                <Switch
                                  size="small"
                                  checked={isLoopStep(agent)}
                                  onChange={(event) =>
                                    handleToggleLoopNode(agent.id, event.target.checked)
                                  }
                                />
                              }
                              label={
                                <Typography variant="caption" sx={{ fontWeight: 600, color: isLoopStep(agent) ? (isDarkMode ? "#c4b5fd" : "#6d28d9") : "text.secondary" }}>
                                  Run for each record
                                </Typography>
                              }
                            />
                          )}
                        </Box>
                        {isPythonExecStep(agent) ? (
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            Python step. Select the node on the canvas to edit timeout.
                          </Typography>
                        ) : isLoopStep(agent) ? (
                          <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                            <Chip
                              size="small"
                              icon={<LoopIcon sx={{ color: "#ffffff !important" }} />}
                              label="LOOP — runs once per record"
                              sx={{
                                backgroundColor: "#6d28d9 !important",
                                color: "#ffffff",
                                fontWeight: 700,
                              }}
                            />
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              The step before it must output a JSON array of records.
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<LoopIcon />}
                              onClick={() => {
                                setSelectedWorkflowNodeId(agent.id);
                                scrollLoopSettingsIntoView();
                              }}
                              sx={{
                                color: isDarkMode ? "#c4b5fd" : "#6d28d9",
                                borderColor: isDarkMode ? "#a78bfa" : "#7c3aed",
                                "&:hover": { borderColor: isDarkMode ? "#c4b5fd" : "#5b21b6" },
                              }}
                            >
                              Loop settings
                            </Button>
                          </Box>
                        ) : (
                          <TextField
                            placeholder="How would you use this agent?"
                            value={agent.description || ""}
                            onChange={(e) => handleAgentDescriptionChange(agent.id, e.target.value)}
                            size="small"
                            variant="outlined"
                            className={styles.agentDescriptionField}
                            inputProps={{ maxLength: 100 }}
                            helperText={`${(agent.description || "").length}/100 characters`}
                          />
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>

            <Box className={styles.section}>
              <Typography variant="body2" className={styles.sectionLabel}>
                Node Palette
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                Drag the calculator onto the canvas. It auto-connects from the selected agent node, or from the only terminal agent if there is just one.
              </Typography>
              <Box
                draggable
                onDragStart={(event) => handlePaletteDragStart(event, PYTHON_EXEC_TYPE)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 2,
                  border: "1px dashed",
                  borderColor: isDarkMode ? "#38bdf8" : "#0284c7",
                  background: isDarkMode
                    ? "linear-gradient(180deg, rgba(8,47,73,0.8) 0%, rgba(15,23,42,0.9) 100%)"
                    : "linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 100%)",
                  cursor: "grab",
                  userSelect: "none",
                }}
              >
                <DragIndicator sx={{ color: isDarkMode ? "#bae6fd" : "#0369a1" }} />
                <Functions sx={{ color: isDarkMode ? "#7dd3fc" : "#0369a1" }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Python Calculator
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Runs Python from the previous agent output
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Flow Visualization */}
            <Box className={styles.flowSection}>
              <Typography variant="body2" className={styles.sectionLabel}>
                Agent Flow Visualization (Drag nodes to reorder, supports parallel execution)
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                Tip: Draw connections to create workflows. Python Calculator nodes must be preceded by an agent and cannot feed another Python Calculator node.
                Loop nodes (purple) run their agent once per record and need a previous step that outputs a JSON array.
              </Typography>
              {invalidConnectionMessage && (
                <Box sx={{
                  p: 1,
                  mb: 1,
                  bgcolor: "error.light",
                  color: "error.contrastText",
                  borderRadius: 1,
                  fontSize: "0.875rem"
                }}>
                  {invalidConnectionMessage}
                </Box>
              )}
              <Box className={styles.flowContainer} ref={reactFlowWrapperRef}>
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onEdgesDelete={onEdgesDelete}
                  onConnect={onConnect}
                  onNodeClick={(event, node) => setSelectedWorkflowNodeId(node.id)}
                  onPaneClick={() => setSelectedWorkflowNodeId(null)}
                  nodeTypes={nodeTypes}
                  edgeTypes={edgeTypes}
                  onInit={setReactFlowInstance}
                  onDragOver={onDragOver}
                  onDrop={onDrop}
                  fitView
                  className={styles.reactFlow}
                  nodesDraggable={true}
                  nodesConnectable={true}
                  elementsSelectable={true}
                  edgesFocusable={true}
                  connectionMode="loose"
                  deleteKeyCode={["Backspace", "Delete"]}
                >
                  <Controls />
                  <MiniMap />
                  <Background variant="dots" gap={12} size={1} />
                </ReactFlow>
              </Box>
              {selectedWorkflowNode && isPythonExecStep(selectedWorkflowNode) && (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: isDarkMode ? "rgba(56, 189, 248, 0.35)" : "rgba(2, 132, 199, 0.25)",
                    backgroundColor: isDarkMode ? "rgba(8, 47, 73, 0.22)" : "rgba(224, 242, 254, 0.45)",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Python Calculator Settings
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
                    Runs Python code from the previous agent. Previous node must emit {"{data, python_code}"} JSON.
                  </Typography>
                  <TextField
                    label="Timeout (seconds)"
                    type="number"
                    size="small"
                    value={readPythonConfigJson(selectedWorkflowNode.configJson).config.timeout_seconds}
                    onChange={(event) =>
                      handlePythonTimeoutChange(selectedWorkflowNode.id, event.target.value)
                    }
                    inputProps={{ min: 1, max: 60 }}
                    helperText="Minimum 1 second, maximum 60 seconds."
                    sx={{ maxWidth: 220 }}
                  />
                  <FormControlLabel
                    sx={{ display: "flex", mt: 1.5, ml: 0 }}
                    control={
                      <Switch
                        size="small"
                        checked={readPythonConfigJson(selectedWorkflowNode.configJson).config.load_files}
                        onChange={(event) =>
                          handlePythonLoadFilesChange(selectedWorkflowNode.id, event.target.checked)
                        }
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Load files
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          Pass uploaded/previous files into the Python runtime.
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              )}
              {selectedWorkflowNode && isLoopStep(selectedWorkflowNode) && (() => {
                const loopConfig = readLoopConfigJson(
                  getLoopConfigJsonString(selectedWorkflowNode)
                );
                const numberFieldSx = { maxWidth: 200 };
                return (
                  <Box
                    ref={loopSettingsPanelRef}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: isDarkMode ? "rgba(167, 139, 250, 0.35)" : "rgba(124, 58, 237, 0.25)",
                      backgroundColor: isDarkMode ? "rgba(46, 16, 101, 0.22)" : "rgba(237, 233, 254, 0.45)",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Loop / For Each Settings — {selectedWorkflowNode.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
                      The previous step must output a JSON array of records. This agent runs once per
                      record. Datasets at/below the inline threshold run inline; larger datasets run
                      as a background batch job.
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
                      <TextField
                        label="Per-record prompt template"
                        value={loopConfig.item_prompt}
                        onChange={(event) =>
                          handleLoopConfigChange(selectedWorkflowNode.id, "item_prompt", event.target.value)
                        }
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        placeholder={'e.g. Scrape {{item.url}} and summarize what {{item.name}} does.'}
                        helperText={'Placeholders: {{item}} (whole record as JSON), {{item.field.subfield}}, {{index}} (1-based), {{total}}. Leave empty to use the node description.'}
                        sx={{ flex: 1 }}
                      />
                      <LoopFieldHelp field="item_prompt" />
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                        <TextField
                          label="Concurrency"
                          type="number"
                          size="small"
                          value={loopConfig.concurrency}
                          onChange={(event) =>
                            handleLoopConfigChange(selectedWorkflowNode.id, "concurrency", event.target.value)
                          }
                          inputProps={{ min: 1, max: 10 }}
                          helperText="Parallel LLM calls per chunk (1–10)"
                          sx={numberFieldSx}
                        />
                        <LoopFieldHelp field="concurrency" />
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                        <TextField
                          label="Micro-batch size"
                          type="number"
                          size="small"
                          value={loopConfig.micro_batch_size}
                          onChange={(event) =>
                            handleLoopConfigChange(selectedWorkflowNode.id, "micro_batch_size", event.target.value)
                          }
                          inputProps={{ min: 1, max: 25 }}
                          helperText="Records per LLM call (1–25). Cheaper, slightly less reliable."
                          sx={numberFieldSx}
                        />
                        <LoopFieldHelp field="micro_batch_size" />
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                        <TextField
                          label="Chunk size (advanced)"
                          type="number"
                          size="small"
                          value={loopConfig.chunk_size}
                          onChange={(event) =>
                            handleLoopConfigChange(selectedWorkflowNode.id, "chunk_size", event.target.value)
                          }
                          inputProps={{ min: 10, max: 200 }}
                          helperText="Records per background task (10–200)"
                          sx={numberFieldSx}
                        />
                        <LoopFieldHelp field="chunk_size" />
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                        <TextField
                          label="Max attempts per record"
                          type="number"
                          size="small"
                          value={loopConfig.max_item_attempts}
                          onChange={(event) =>
                            handleLoopConfigChange(selectedWorkflowNode.id, "max_item_attempts", event.target.value)
                          }
                          inputProps={{ min: 1, max: 5 }}
                          helperText="Retries before a record is marked failed (1–5)"
                          sx={numberFieldSx}
                        />
                        <LoopFieldHelp field="max_item_attempts" />
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                        <TextField
                          label="Max items"
                          type="number"
                          size="small"
                          value={loopConfig.max_items}
                          onChange={(event) =>
                            handleLoopConfigChange(selectedWorkflowNode.id, "max_items", event.target.value)
                          }
                          inputProps={{ min: 1, max: 50000 }}
                          helperText="Hard cap; extra records are dropped (1–50000)"
                          sx={numberFieldSx}
                        />
                        <LoopFieldHelp field="max_items" />
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                        <TextField
                          label="Inline threshold"
                          type="number"
                          size="small"
                          value={loopConfig.inline_threshold}
                          onChange={(event) =>
                            handleLoopConfigChange(selectedWorkflowNode.id, "inline_threshold", event.target.value)
                          }
                          inputProps={{ min: 0, max: 100 }}
                          helperText="Datasets at/below this size run inline, no background job (0–100)"
                          sx={numberFieldSx}
                        />
                        <LoopFieldHelp field="inline_threshold" />
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                        <FormControl size="small" sx={numberFieldSx}>
                          <InputLabel>On record error</InputLabel>
                          <Select
                            value={loopConfig.on_error}
                            label="On record error"
                            onChange={(event) =>
                              handleLoopConfigChange(selectedWorkflowNode.id, "on_error", event.target.value)
                            }
                          >
                            <MenuItem value="skip">Skip — record failure, keep going</MenuItem>
                            <MenuItem value="fail_job">Fail job — stop on first permanent failure</MenuItem>
                          </Select>
                        </FormControl>
                        <LoopFieldHelp field="on_error" />
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                        <FormControl size="small" sx={numberFieldSx}>
                          <InputLabel>Sink mode</InputLabel>
                          <Select
                            value={loopConfig.sink_mode}
                            label="Sink mode"
                            onChange={(event) =>
                              handleLoopConfigChange(selectedWorkflowNode.id, "sink_mode", event.target.value)
                            }
                          >
                            <MenuItem value="post_chain">Post-chain — nodes after the loop run as sink</MenuItem>
                            <MenuItem value="none">None — results only via download API</MenuItem>
                          </Select>
                        </FormControl>
                        <LoopFieldHelp field="sink_mode" />
                      </Box>
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions className={styles.dialogActions}>
          <Button
            onClick={handleCancel}
            variant="text"
            className={styles.cancelButton}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            className={styles.saveButton}
            startIcon={<PlayArrow />}
          >
            Save Chain
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default AgentChainingDialog;
